import api, { handleApiResponse, getCsrfToken } from './api';
import { User, AuthResponse, RegistrationData } from '@/types';

export const authService = {
  async login(email: string, password: string): Promise<AuthResponse> {
    console.log('🔐 Attempting login with:', { email, passwordLength: password.length });
    
    try {
      // Get CSRF token first
      console.log('Fetching CSRF token...');
      const csrfSuccess = await getCsrfToken();
      console.log('CSRF token fetch result:', csrfSuccess);
      console.log('Current cookies:', document.cookie);
      
      // Make login request
      console.log('Making login request to /api/login...');
      const response = await api.post('/api/login', { 
        email, 
        password 
      });
      
      console.log('Login response:', {
        status: response.status,
        data: response.data,
        headers: response.headers
      });
      
      if (response.data) {
        // Store the token
        console.log('Storing auth token:', response.data.token);
        localStorage.setItem('auth_token', response.data.token);
        // Store the user
        console.log('Storing user data:', response.data.user);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        return response.data;
      }
      
      throw new Error('Invalid response format');
    } catch (error: any) {
      console.error('💥 Login error occurred');
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        stack: error.stack,
        headers: error.response?.headers,
        config: error.config
      });
      
      // Handle network errors
      if (!error.response) {
        console.error('🌐 Network error - no response received');
        throw new Error('Network error - please check your connection and try again');
      }
      
      // Handle CSRF token errors
      if (error.response.status === 419) {
        console.error('CSRF token error - refreshing token');
        await getCsrfToken(); // Try to refresh the CSRF token
        throw new Error('Session expired. Please refresh the page and try again.');
      }
      
      // Handle validation errors
      if (error.response.status === 422) {
        const validationErrors = error.response.data?.errors;
        if (validationErrors) {
          console.error('Validation errors:', validationErrors);
          const firstError = Object.values(validationErrors)[0];
          throw new Error(Array.isArray(firstError) ? firstError[0] : firstError as string);
        }
      }
      
      // Handle unauthorized
      if (error.response.status === 401) {
        console.error('Unauthorized error:', error.response.data);
        throw new Error(error.response.data?.message || 'Invalid credentials');
      }
      
      // Handle other errors
      throw new Error(error.response?.data?.message || error.message || 'Login failed');
    }
  },

  async register(registrationData: RegistrationData | FormData): Promise<any> {
    console.log('Attempting registration with:', registrationData);
    
    try {
      // If registrationData is FormData, convert boolean fields
      if (registrationData instanceof FormData) {
        // Debug: Log the original terms_accepted value
        const originalTerms = registrationData.get('terms_accepted');
        console.log('Original terms_accepted value:', originalTerms, typeof originalTerms);
        
        // Convert 'terms_accepted' to boolean
        const termsAccepted = registrationData.get('terms_accepted');
        const termsValue = termsAccepted === 'true' ? 'true' : 'false';
        registrationData.set('terms_accepted', termsValue);
        console.log('Set terms_accepted to:', termsValue, typeof termsValue);
        
        // Convert 'auto_payment' to boolean
        const autoPayment = registrationData.get('auto_payment');
        registrationData.set('auto_payment', autoPayment === 'true' ? 'true' : 'false');
        
        // Debug: Log the final FormData
        console.log('Final FormData entries:');
        for (const pair of registrationData.entries()) {
          console.log(pair[0], pair[1], typeof pair[1]);
        }
        
        const response = await api.post('/api/register', registrationData);
        console.log('Registration response:', response);
        
        // Handle the specific API response format
        if (response.data.status === 'success' || response.data.success) {
          const userData = response.data.data || response.data;
          
          // Process user data to handle name field
          if (userData.user) {
            const userInfo = userData.user;
            return {
              user: {
                id: userInfo.id,
                name: `${userInfo.first_name} ${userInfo.last_name}`,
                email: userInfo.email,
                role: userInfo.role,
              },
              token: userData.token,
            };
          }
          return userData;
        }
        throw new Error(response.data.message || 'Registration failed');
      }

      // If registrationData is a regular object, format it
      const formattedData = {
        first_name: registrationData.first_name?.trim(),
        last_name: registrationData.last_name?.trim(),
        email: registrationData.email?.trim().toLowerCase(),
        phone: registrationData.phone?.trim(),
        dob: registrationData.dob,
        nationality: registrationData.nationality?.trim(),
        id_passport: registrationData.id_passport?.trim(),
        residential_address: registrationData.residential_address?.trim(),
        occupation: registrationData.occupation?.trim() || '',
        employer: registrationData.employer?.trim() || '',
        membership_type: registrationData.membership_type,
        playing_position: registrationData.playing_position || '',
        playing_level: registrationData.playing_level,
        jersey_number: registrationData.jersey_number || null,
        emergency_contact_name: registrationData.emergency_contact_name?.trim(),
        emergency_contact_relationship: registrationData.emergency_contact_relationship,
        emergency_contact_phone: registrationData.emergency_contact_phone?.trim(),
        medical_conditions: registrationData.medical_conditions?.trim() || '',
        allergies: registrationData.allergies?.trim() || '',
        insurance_provider: registrationData.insurance_provider?.trim() || '',
        password: registrationData.password,
        password_confirmation: registrationData.password_confirmation,
        terms_accepted: Boolean(registrationData.terms_accepted),
        auto_payment: Boolean(registrationData.auto_payment),
        payment_phone: registrationData.payment_phone?.trim(),
        payment_provider: registrationData.payment_provider
      };

      console.log('Formatted registration data:', formattedData);
      console.log('Terms accepted value in formatted data:', formattedData.terms_accepted, typeof formattedData.terms_accepted);
      
      const response = await api.post('/api/register', formattedData);
      console.log('Registration response:', response);
      
      // Handle the specific API response format
      if (response.data.status === 'success' || response.data.success) {
        const userData = response.data.data || response.data;
        
        // Process user data to handle name field
        if (userData.user) {
          const userInfo = userData.user;
          if (userInfo.name && !userInfo.first_name && !userInfo.last_name) {
            // Split name into first and last name
            const nameParts = userInfo.name.trim().split(' ');
            userInfo.first_name = nameParts[0] || '';
            userInfo.last_name = nameParts.slice(1).join(' ') || '';
          } else if (userInfo.first_name && userInfo.last_name && !userInfo.name) {
            // Create name from first and last name
            userInfo.name = `${userInfo.first_name} ${userInfo.last_name}`.trim();
          }
          userData.user = userInfo;
        }
        
        // Store user data if available
        if (userData.user) {
          localStorage.setItem('user', JSON.stringify(userData.user));
        }
        
        // Store token if provided
        if (userData.token) {
          localStorage.setItem('auth_token', userData.token);
        }
        
        console.log('Registration successful:', response.data);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Registration failed');
      }
    } catch (error: any) {
      console.error('Registration error details:', error.response?.data);
      
      if (error.response?.status === 422) {
        // Handle validation errors
        const errors = error.response.data?.errors;
        if (errors) {
          const firstError = Object.values(errors)[0];
          const errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
          throw new Error(errorMessage);
        } else {
          throw new Error(error.response.data?.message || 'Validation error occurred');
        }
      }
      
      throw new Error(error.response?.data?.message || error.message || 'Registration failed');
    }
  },

  async logout(): Promise<void> {
    try {
      await api.post('/api/logout');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local storage even if API call fails
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      throw error;
    }
  },

  async getCurrentUser(): Promise<User> {
    try {
      const response = await api.get('/api/user');
      return response.data;
    } catch (error) {
      console.error('Failed to get current user:', error);
      throw error;
    }
  },

  async forgotPassword(email: string): Promise<void> {
    console.log('🔄 Sending forgot password request for:', email);
    try {
      const response = await api.post('/forgot-password', { email });
      console.log('✅ Forgot password response:', response.data);
      
      // Handle successful response
      if (response.data.success || response.data.status === 'success' || response.status === 200) {
        return;
      } else {
        throw new Error(response.data.message || 'Failed to send reset email');
      }
    } catch (error: any) {
      console.error('❌ Forgot password error:', error.response?.data || error.message);
      
      // Handle specific error cases
      if (error.response?.status === 404) {
        throw new Error('Email address not found. Please check the email address and try again.');
      } else if (error.response?.status === 422) {
        const errorMessage = error.response.data?.message || 
                           error.response.data?.errors?.email?.[0] || 
                           'Invalid email address format';
        throw new Error(errorMessage);
      } else if (error.response?.status === 429) {
        throw new Error('Too many reset requests. Please wait before trying again.');
      }
      
      throw new Error(error.response?.data?.message || error.message || 'Failed to send reset email');
    }
  },

  async resetPassword(token: string, email: string, password: string): Promise<void> {
    console.log('🔄 Sending reset password request');
    try {
      const response = await api.post('/reset-password', {
        token,
        email,
        password,
        password_confirmation: password,
      });
      console.log('✅ Reset password response:', response.data);
      
      // Handle successful response
      if (response.data.success || response.data.status === 'success' || response.status === 200) {
        return;
      } else {
        throw new Error(response.data.message || 'Failed to reset password');
      }
    } catch (error: any) {
      console.error('❌ Reset password error:', error.response?.data || error.message);
      
      // Handle specific error cases
      if (error.response?.status === 400 || error.response?.status === 404) {
        throw new Error('Invalid or expired reset token. Please request a new password reset.');
      } else if (error.response?.status === 422) {
        const errorMessage = error.response.data?.message || 
                           error.response.data?.errors?.password?.[0] || 
                           'Password validation failed';
        throw new Error(errorMessage);
      }
      
      throw new Error(error.response?.data?.message || error.message || 'Failed to reset password');
    }
  },

  async validateResetToken(token: string, email: string): Promise<void> {
    console.log('🔄 Validating reset token');
    try {
      const response = await api.post('/validate-reset-token', {
        token,
        email,
      });
      console.log('✅ Token validation response:', response.data);
      
      // Handle successful response
      if (response.data.success || response.data.status === 'success' || response.status === 200) {
        return;
      } else {
        throw new Error(response.data.message || 'Token validation failed');
      }
    } catch (error: any) {
      console.error('❌ Token validation error:', error.response?.data || error.message);
      
      // Handle specific error cases
      if (error.response?.status === 400 || error.response?.status === 404) {
        throw new Error('Invalid or expired reset token');
      }
      
      throw new Error(error.response?.data?.message || error.message || 'Token validation failed');
    }
  },

  getStoredUser(): User | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  getStoredToken(): string | null {
    return localStorage.getItem('auth_token');
  },

  isAuthenticated(): boolean {
    return !!this.getStoredToken();
  },

  isAdmin(): boolean {
    const user = this.getStoredUser();
    return user?.role === 'admin';
  },

  isMember(): boolean {
    const user = this.getStoredUser();
    return user?.role === 'member';
  },

  async changePassword(currentPassword: string, newPassword: string, confirmPassword: string): Promise<void> {
    try {
      const response = await api.post('/api/change-password', {
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: confirmPassword
      });
      return handleApiResponse(response);
    } catch (error: any) {
      console.error('Change password error:', error);
      throw new Error(error.response?.data?.message || 'Failed to change password');
    }
  }
};
